import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { finalize, firstValueFrom } from 'rxjs';
import { CustomClaimService } from '../../core/services/custom-claim.service';
import { ToastService } from '../../core/services/toast.service';
import { MembershipRoles } from '../../core/models/membership.model';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal.component';
import { CertificationsCardComponent } from '../../shared/components/certifications-card/certifications-card.component';
import { UserProfile } from 'src/app/core/models/user-profile.model';
import { UserService } from 'src/app/core/services/user.service';
import { LoginPlatform, getLoginPlatformBadge } from '../../shared/utils/login-platform.utils';
import { getMembershipPlanBadge } from '../../shared/utils/membership-plan.utils';
import { NgxMaskDirective } from 'ngx-mask';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { toNationalDigits } from 'src/app/shared/utils/format.utils';

type ConfirmAction =
  | 'verify-email'
  | 'unverify-email'
  | 'reset-password'
  | 'lock-account'
  | 'unlock-account'
  | 'password-reset-flag';

interface LockedStatus {
  isLocked: boolean;
  firebaseDisabled: boolean;
  isAutoLocked: boolean;
  // ISO 8601 string from JSON; parse to Date at the call site if needed.
  accountLockedUntil: string | null;
  failedLoginAttempts: number;
}

/**
 * Unified invitation from GET /admin/users/:userId/invitations
 * Contains invitations from both adn_profile_invitations and adn_organization_invitations
 */
interface UserInvitation {
  id: string;
  source: 'organization' | 'profile';
  email: string;
  status: string;
  type: string;
  role?: number;
  expiresAt: string;
  emailStatus: string;
  resendCount: number;
  organizationName?: string;
  createdAt: string;
}

interface MembershipClaim {
  organizationId: string;
  organizationName: string;
  claimsBits: number;
  roles: string[];
  /** This organization's own live Core/Pro tier — resolved per-org, not the profile-level display cache. */
  membershipPlan: 'core' | 'pro';
}

interface UserClaims {
  globalClaimsBits: number;
  globalRoles: string[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
  memberships: MembershipClaim[];
}

@Component({
  selector: 'app-user-detail',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, ConfirmationModalComponent, CertificationsCardComponent, NgxMaskDirective, PhoneFormatPipe],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.scss'
})
export class UserDetailComponent implements OnInit {
  userId!: string;
  user: UserProfile | null = null;
  editForm: FormGroup;
  loading = true;
  saving = false;
  errorMessage = '';
  successMessage = '';
  editMode = false;
  isLocked = false;
  loadingLockStatus = false;

  // Claims-related properties
  userClaims: UserClaims | null = null;
  loadingClaims = false;
  togglingAdmin = false;
  currentUserIsSuperAdmin = false;

  // Shared confirmation modal state (email verification, password reset, account lock)
  showConfirmModal = false;
  confirmModalAction: ConfirmAction | null = null;
  confirmModalProcessing = false;

  // Invitation-related properties
  invitations: UserInvitation[] = [];
  loadingInvitations = false;
  resendingInvitationId: string | null = null;
  sendingInvitationId: string | null = null;
  cancellingInvitationId: string | null = null;
  deletingInvitationId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private http: HttpClient,
    private customClaimService: CustomClaimService,
    private toastService: ToastService,
    private userService: UserService,
  ) {
    this.editForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: ['']
    });

    // Check if current user is super admin
    this.currentUserIsSuperAdmin = this.customClaimService.isSuperAdmin();
  }

  async ngOnInit(): Promise<void> {
    this.userId = this.route.snapshot.params['id'];
    await Promise.all([
      this.loadUser(),
      this.loadUserClaims(),
    ]);
  }

  loadUser(): void {
    this.loading = true;
    this.errorMessage = '';

    this.userService.getUser(this.userId)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (user) => {
          if (user === null) {
            this.errorMessage = 'Failed to load user details';
            return;
          }

          this.user = user;
          // Seed lock state from the user payload so the Lock/Unlock button
          // has a sane value before the dedicated lock-status check resolves.
          this.isLocked = !!user.isLocked;
          this.populateForm();
          this.checkLockStatus();
          // Load all invitations for this user (both profile and org invitations)
          this.loadInvitations();
        },
        error: (err) => {
          console.error('Load user error:', err);
          this.errorMessage = 'Failed to load user details';
        }
      });
  }

  async checkLockStatus(): Promise<void> {
    this.loadingLockStatus = true;

    try {
      const response = await firstValueFrom(
        this.http.get<{ lockedStatus: LockedStatus }>(`${environment.apiUrl}/admin/users/${this.userId}/lock-status`)
      );

      this.isLocked = response.lockedStatus.isLocked;

      // Keep the displayed counters in sync with the live truth.
      if (this.user) {
        this.user.failedLoginAttempts = response.lockedStatus.failedLoginAttempts;
        this.user.lockedUntil = response.lockedStatus.accountLockedUntil;
        this.user.isLocked = response.lockedStatus.isLocked;
      }
    } catch (error: any) {
      console.error('Lock status error:', error);
      // Non-blocking — leave the page-level error/loading alone so the
      // rest of the user detail view still renders cleanly.
    } finally {
      this.loadingLockStatus = false;
    }
  }

  openPasswordResetModal(): void {
    if(!this.user) return;
    this.confirmModalAction = 'password-reset-flag'
    this.showConfirmModal = true
  }

  populateForm(): void {
    if (this.user) {
      this.editForm.patchValue({
        email: this.user.email,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        phone: toNationalDigits(this.user.phone)
      });
    }
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (!this.editMode) {
      this.populateForm(); // Reset form on cancel
    }
  }

  async onSubmit(): Promise<void> {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const updates = this.editForm.value;

      const response = await firstValueFrom(
        this.http.put<{ user: Partial<UserProfile> }>(`${environment.apiUrl}/admin/users/${this.userId}`, updates)
      );

      if (this.user && response.user) {
        this.user.firstName = response.user.firstName || this.user.firstName;
        this.user.lastName = response.user.lastName || this.user.lastName;
        this.user.email = response.user.email || this.user.email;
        this.user.phone = response.user.phone ?? this.user.phone;
        this.user.updatedAt = response.user.updatedAt || new Date().toISOString();
      }

      this.toastService.success('User updated successfully');
      this.editMode = false;
    } catch (error: any) {
      console.error('Update user error:', error);
      this.toastService.error('Failed to update user');
    } finally {
      this.saving = false;
    }
  }

  openLockConfirm(): void {
    if (!this.user) return;
    this.confirmModalAction = this.isLocked ? 'unlock-account' : 'lock-account';
    this.showConfirmModal = true;
  }

  private async toggleAccountLock(): Promise<void> {
    if (!this.user) return;

    const action = this.isLocked ? 'unlock' : 'lock';

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean, message?: string}>(`${environment.apiUrl}/admin/users/${this.userId}/${action}-account`, {})
      );

      if (response.success) {
        this.isLocked = !this.isLocked;
        this.toastService.success(`Account ${this.isLocked ? 'locked' : 'unlocked'} successfully`);
        // Refresh lock-status so failedLoginAttempts / lockedUntil reflect
        // the post-action state instead of stale pre-action values.
        await this.checkLockStatus();
      } else {
        this.toastService.error(response.message || `Failed to ${action} account`);
      }
    } catch (error) {
      console.error('Toggle lock error:', error);
      this.toastService.error(`Failed to ${action} account`);
    }
  }

  openEmailVerificationConfirm(): void {
    if (!this.user) return;
    this.confirmModalAction = this.user.emailVerified ? 'unverify-email' : 'verify-email';
    this.showConfirmModal = true;
  }

  private async toggleEmailVerification(): Promise<void> {
    if (!this.user) return;

    const newStatus = !this.user.emailVerified;

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; error?: string }>(
          `${environment.apiUrl}/admin/users/${this.userId}/toggle-email-verification`,
          { emailVerified: newStatus }
        )
      );

      if (response.success) {
        this.user.emailVerified = newStatus;
        this.toastService.success(`Email ${newStatus ? 'verified' : 'unverified'} successfully`);
      } else {
        this.toastService.error(response.error || 'Failed to update email verification');
      }
    } catch (error: any) {
      console.error('Toggle email verification error:', error);
      this.toastService.error(error.error?.error || 'Failed to update email verification');
    }
  }

  async resendVerificationEmail(): Promise<void> {
    if (!this.user) return;

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; error?: string }>(
          `${environment.apiUrl}/admin/users/${this.userId}/resend-verification`, {}
        )
      );

      if (response.success) {
        this.toastService.success('Verification email sent successfully');
      } else {
        this.toastService.error(response.error || 'Failed to send verification email');
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      this.toastService.error(error.error?.error || 'Failed to send verification email');
    }
  }

  openPasswordResetConfirm(): void {
    if (!this.user) return;
    this.confirmModalAction = 'reset-password';
    this.showConfirmModal = true;
  }

  private async resetPassword(): Promise<void> {
    if (!this.user) return;

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; error?: string }>(`${environment.apiUrl}/admin/users/${this.userId}/send-reset-password`, {})
      );

      if (response.success) {
        this.toastService.success('Password reset email sent successfully');
      } else {
        this.toastService.error(response.error || 'Failed to send password reset email');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      this.toastService.error('Failed to send password reset email');
    }
  }

  async onConfirmAction(): Promise<void> {
    if (!this.confirmModalAction) return;

    this.confirmModalProcessing = true;
    try {
      switch (this.confirmModalAction) {
        case 'verify-email':
        case 'unverify-email':
          await this.toggleEmailVerification();
          break;
        case 'reset-password':
          await this.resetPassword();
          break;
        case 'lock-account':
        case 'unlock-account':
          await this.toggleAccountLock();
          break;
        case 'password-reset-flag':
          await this.setPasswordResetFlag(!this.user?.requiresPasswordReset);
          break;
      }
    } finally {
      this.confirmModalProcessing = false;
      this.showConfirmModal = false;
      this.confirmModalAction = null;
    }
  }

  onCancelConfirm(): void {
    this.showConfirmModal = false;
    this.confirmModalAction = null;
  }

  async setPasswordResetFlag(value: boolean = true): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';
  
    try {
      const user = await firstValueFrom(
        this.userService.requirePasswordReset(this.userId, value)
      );
      this.user = user;
      this.toastService.success('User must reset their password before signing in.');
    } catch (error) {
      this.errorMessage = 'Failed to require password reset';
      console.error('Require password reset error:', error);
    }
  }
  

  async disable2FA(): Promise<void> {
    if (!this.user || !this.user.is2faEnabled) return;

    if (!confirm('Are you sure you want to disable 2FA for this user? This will require admin approval.')) {
      return;
    }

    try {
      // TODO: Replace with actual API call
      // await firstValueFrom(
      //   this.http.post(`${environment.apiUrl}/admin/users/${this.userId}/disable-2fa`, {})
      // );

      // Mock success
      this.user.is2faEnabled = false;
      this.user.twoFactorMethod = null;
      this.toastService.success('2FA disabled successfully');
    } catch (error) {
      console.error('Disable 2FA error:', error);
      this.toastService.error('Failed to disable 2FA');
    }
  }

  // =========================================
  // Invitation Management
  // =========================================

  /**
   * Load all invitations for this user from both profile and org invitation tables.
   * Uses the unified GET /admin/users/:userId/invitations endpoint.
   */
  async loadInvitations(): Promise<void> {
    this.loadingInvitations = true;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: { invitations: UserInvitation[] } }>(
          `${environment.apiUrl}/admin/users/${this.userId}/invitations`
        )
      );

      if (response.success && response.data?.invitations) {
        this.invitations = response.data.invitations;
      }
    } catch (error: any) {
      console.error('Load invitations error:', error);
      // Non-critical -- do not show error to user
    } finally {
      this.loadingInvitations = false;
    }
  }

  /**
   * Get actionable invitations (pending or expired, excludes accepted/cancelled)
   */
  get pendingInvitations(): UserInvitation[] {
    return this.invitations.filter(
      inv => inv.status === 'pending' || inv.status === 'expired'
    );
  }

  /**
   * Whether user has any organization memberships
   * When true, invitation list is read-only
   */
  get hasOrganizations(): boolean {
    return (this.userClaims?.memberships?.length ?? 0) > 0;
  }

  /**
   * Resend an invitation
   */
  async resendInvitation(invitation: UserInvitation): Promise<void> {
    this.resendingInvitationId = invitation.id;

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; error?: string; message?: string }>(
          `${environment.apiUrl}/admin/invitations/${invitation.id}/resend`,
          {}
        )
      );

      if (response.success) {
        this.toastService.success('Invitation resent successfully');
        // Reload invitations to get updated data
        await this.loadInvitations();
      } else {
        this.toastService.error(response.error || 'Failed to resend invitation');
      }
    } catch (error: any) {
      console.error('Resend invitation error:', error);
      this.toastService.error(error.error?.error || 'Failed to resend invitation');
    } finally {
      this.resendingInvitationId = null;
    }
  }

  async sendInvitation(invitation: UserInvitation): Promise<void> {
    this.sendingInvitationId = invitation.id;

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; error?: string; message?: string }>(
          `${environment.apiUrl}/admin/invitations/${invitation.id}/send-email`,
          {}
        )
      );

      if (response.success) {
        this.toastService.success('Invitation sent successfully');
        await this.loadInvitations();
      } else {
        this.toastService.error(response.error || 'Failed to send invitation');
      } 
    } catch (error: any) {
      console.error('Send Invitation error:', error);
      this.toastService.error(error.error?.error || 'Failed to send invitation');
    } finally {
      this.sendingInvitationId = null;
    }
  }

  /**
   * Cancel an invitation
   */
  async cancelInvitation(invitation: UserInvitation): Promise<void> {
    if (!confirm(`Cancel this invitation for ${invitation.email}?`)) {
      return;
    }

    this.cancellingInvitationId = invitation.id;

    try {
      const response = await firstValueFrom(
        this.http.delete<{ success: boolean; error?: string }>(
          `${environment.apiUrl}/admin/invitations/${invitation.id}`
        )
      );

      if (response.success) {
        this.toastService.success('Invitation cancelled successfully');
        await this.loadInvitations();
      } else {
        this.toastService.error(response.error || 'Failed to cancel invitation');
      }
    } catch (error: any) {
      console.error('Cancel invitation error:', error);
      this.toastService.error(error.error?.error || 'Failed to cancel invitation');
    } finally {
      this.cancellingInvitationId = null;
    }
  }

  async deleteInvitation(invitation: UserInvitation): Promise<void> {
    if (!confirm(`Delete this invitation for ${invitation.email}?`)) {
      return;
    }

    this.deletingInvitationId = invitation.id;

    try {
      const response = await firstValueFrom(
        this.http.delete<{ success: boolean; error?: string }>(
          `${environment.apiUrl}/admin/invitations/${invitation.id}/delete`
        )
      );

      if (response.success) {
        this.toastService.success('Invitation deleted successfully');
        await this.loadInvitations();
      } else {
        this.toastService.error(response.error || 'Failed to delete invitation');
      }
    } catch (error: any) {
      console.error('Delete invitation error:', error);
      this.toastService.error(error.error?.error || 'Failed to delete invitation');
    } finally {
      this.deletingInvitationId = null;
    }
  }

  /**
   * Get source badge class
   */
  getSourceBadgeClass(source: string): string {
    return source === 'organization' ? 'bg-primary' : 'bg-info';
  }

  /**
   * Get source display text
   */
  getSourceText(source: string): string {
    return source === 'organization' ? 'Organization' : 'Profile';
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'pending': return 'bg-warning text-dark';
      case 'accepted': return 'bg-success';
      case 'cancelled': return 'bg-secondary';
      case 'expired': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  /**
   * Get email status badge class
   */
  getEmailStatusBadgeClass(status: string): string {
    switch (status) {
      case 'sent': return 'bg-success';
      case 'failed': return 'bg-danger';
      case 'never_sent':
      default: return 'bg-secondary';
    }
  }

  /**
   * Get email status display text
   */
  getEmailStatusText(status: string): string {
    switch (status) {
      case 'sent': return 'Email Sent';
      case 'failed': return 'Email Failed';
      case 'never_sent':
      default: return 'Not Sent';
    }
  }

  /**
   * Check if invitation is expired
   */
  isInvitationExpired(invitation: UserInvitation): boolean {
    if (!invitation.expiresAt) return false;
    return new Date() > new Date(invitation.expiresAt);
  }

  formatInvitationType(type: string | null, invitation?: UserInvitation): string {
    if (!type) {
      // For organization invitations, show role instead
      if (invitation?.source === 'organization' && invitation.role != null) {
        if ((invitation.role & MembershipRoles.PilotInCharge) === MembershipRoles.PilotInCharge) return 'Pilot in Command';
        if ((invitation.role & MembershipRoles.Pilot) === MembershipRoles.Pilot) return 'Pilot';
        return 'Member';
      }
      return 'Organization Invite';
    }
    return type.split('_').join(' ');
  }

  goBack(): void {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      this.router.navigateByUrl(returnUrl);
      return;
    }
    this.router.navigate(['/users']);
  }

  goBackText(): string {
    return this.route.snapshot.queryParamMap.get('returnLabel') ?? 'Users';
  }

  readonly getLoginPlatformBadge = getLoginPlatformBadge;
  readonly getMembershipPlanBadge = getMembershipPlanBadge;

  formatDate(dateString: string | null): string {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // =========================================
  // Roles & Permissions
  // =========================================

  async loadUserClaims(): Promise<void> {
    this.loadingClaims = true;

    try {
      const response = await firstValueFrom(
        this.http.get<{ claims: UserClaims }>(`${environment.apiUrl}/admin/users/${this.userId}/claims`)
      );

      this.userClaims = response.claims;
    } catch (error: any) {
      console.error('Load user claims error:', error);
      // Don't show error for claims - user profile still loaded
    } finally {
      this.loadingClaims = false;
    }
  }

  async confirmToggleAdminRole(): Promise<void> {
    if (!this.userClaims || !this.currentUserIsSuperAdmin) return;

    const newAdminStatus = !this.userClaims.isAdmin;

    this.togglingAdmin = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; isAdmin: boolean; isSuperAdmin: boolean }>(
          `${environment.apiUrl}/admin/users/${this.userId}/admin-role`,
          { isAdmin: newAdminStatus }
        )
      );

      if (response.success) {
        this.userClaims.isAdmin = response.isAdmin;
        this.userClaims.isSuperAdmin = response.isSuperAdmin;

        // Update global roles display
        this.userClaims.globalRoles = [];
        if (response.isAdmin) this.userClaims.globalRoles.push('Admin');
        if (response.isSuperAdmin) this.userClaims.globalRoles.push('Super Admin');

        this.toastService.success(
          newAdminStatus ? 'Admin access granted successfully' : 'Admin access revoked successfully'
        );

        // Close the modal
        this.closeModal('adminRoleModal');
      }
    } catch (error: any) {
      console.error('Toggle admin role error:', error);
      this.toastService.error(error.error?.error || 'Failed to update admin role');
    } finally {
      this.togglingAdmin = false;
    }
  }

  private closeModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      // Use Bootstrap's modal API to close it
      const modalInstance = (window as any).bootstrap?.Modal?.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      } else {
        // Fallback: manually trigger close button
        const closeBtn = modalElement.querySelector('[data-bs-dismiss="modal"]') as HTMLElement;
        closeBtn?.click();
      }
    }
  }
}
